<script lang="ts">
  import { onMount } from 'svelte';
  import { _ } from 'svelte-i18n';
  import { push } from 'svelte-spa-router';
  import { IconArrowLeft } from '@tabler/icons-svelte';
  import { getLicenses } from '../../utils/licenses';
  import type { PackageLicense } from '../../utils/licenses';

  export let embedded = false;

  let licenses: PackageLicense[] = [];
  let loading = true;

  onMount(async () => {
    licenses = await getLicenses();
    loading = false;
  });

  function groupByLicense(packages: PackageLicense[]): Record<string, PackageLicense[]> {
    const grouped: Record<string, PackageLicense[]> = {};
    packages.forEach((pkg) => {
      const license = pkg.license || 'Unknown';
      if (!grouped[license]) {
        grouped[license] = [];
      }
      grouped[license].push(pkg);
    });
    return grouped;
  }
</script>

{#if !embedded}
  <div class="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-slate-50 dark:from-slate-900 dark:via-purple-900 dark:to-slate-900 flex items-center justify-center p-4 transition-colors duration-300">
    <div class="w-full max-w-4xl">
      <!-- Back Button -->
      <button
        on:click={() => push('/')}
        class="glass-button flex items-center gap-2 px-4 py-2 rounded-lg mb-6 text-slate-900 dark:text-white"
        aria-label="Back to home"
      >
        <IconArrowLeft class="w-5 h-5" />
        <span>{$_('pages.back')}</span>
      </button>

      <!-- Licenses Card -->
      <div class="glass-card p-8 md:p-12 rounded-3xl shadow-2xl">
        <h1 class="text-3xl md:text-4xl font-bold text-custom-heading dark:text-white text-center mb-8">
          {$_(`pages.licenses.title`)}
        </h1>

        {#if loading}
          <div class="text-center py-12">
            <div class="text-custom dark:text-white text-lg">{$_(`pages.licenses.loading`)}</div>
          </div>
        {:else}
          <div class="prose prose-slate dark:prose-invert max-w-none text-custom dark:text-white">
            <section class="mb-8">
              <p class="text-custom dark:text-white mb-6">
                {$_(`pages.licenses.intro`)}
              </p>
            </section>

            <section class="mb-8">
              <h2 class="text-2xl font-semibold mb-4 text-custom-heading dark:text-white">
                {$_(`pages.licenses.thanks.title`)}
              </h2>
              <p class="text-custom dark:text-white mb-6">
                {$_(`pages.licenses.thanks.content`)}
              </p>
            </section>

            <section class="mb-8">
              <h2 class="text-2xl font-semibold mb-4 text-custom-heading dark:text-white">
                {$_(`pages.licenses.packages.title`)}
              </h2>
              
              {#each Object.entries(groupByLicense(licenses)) as [licenseType, packages]}
                <div class="mb-6">
                  <h3 class="text-xl font-semibold mb-3 text-custom-heading dark:text-white">
                    {licenseType}
                  </h3>
                  <div class="space-y-2">
                    {#each packages as pkg}
                      <div class="flex flex-wrap items-center gap-2 p-3 bg-white/20 dark:bg-white/5 rounded-lg">
                        <span class="font-medium text-custom dark:text-white">{pkg.name}</span>
                        <span class="text-sm text-custom-secondary dark:text-white/70">v{pkg.version}</span>
                        {#if pkg.url}
                          <a
                            href={pkg.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            class="text-sm text-green-600 dark:text-green-400 hover:underline ml-auto"
                          >
                            {$_(`pages.licenses.packages.link`)}
                          </a>
                        {/if}
                      </div>
                    {/each}
                  </div>
                </div>
              {/each}
            </section>
          </div>
        {/if}
      </div>
    </div>
  </div>
{:else}
  <!-- Licenses Content (embedded, ohne Card) -->
  <div class="p-8 md:p-12">
    <h1 class="text-3xl md:text-4xl font-bold text-custom-heading dark:text-white text-center mb-8">
      {$_(`pages.licenses.title`)}
    </h1>

    {#if loading}
      <div class="text-center py-12">
        <div class="text-custom dark:text-white text-lg">{$_(`pages.licenses.loading`)}</div>
      </div>
    {:else}
      <div class="prose prose-slate dark:prose-invert max-w-none text-custom dark:text-white">
        <section class="mb-8">
          <p class="text-custom dark:text-white mb-6">
            {$_(`pages.licenses.intro`)}
          </p>
        </section>

        <section class="mb-8">
          <h2 class="text-2xl font-semibold mb-4 text-custom-heading dark:text-white">
            {$_(`pages.licenses.thanks.title`)}
          </h2>
          <p class="text-custom dark:text-white mb-6">
            {$_(`pages.licenses.thanks.content`)}
          </p>
        </section>

        <section class="mb-8">
          <h2 class="text-2xl font-semibold mb-4 text-custom-heading dark:text-white">
            {$_(`pages.licenses.packages.title`)}
          </h2>
          
          {#each Object.entries(groupByLicense(licenses)) as [licenseType, packages]}
            <div class="mb-6">
              <h3 class="text-xl font-semibold mb-3 text-custom-heading dark:text-white">
                {licenseType}
              </h3>
              <div class="space-y-2">
                {#each packages as pkg}
                  <div class="flex flex-wrap items-center gap-2 p-3 bg-white/20 dark:bg-white/5 rounded-lg">
                    <span class="font-medium text-custom dark:text-white">{pkg.name}</span>
                    <span class="text-sm text-custom-secondary dark:text-white/70">v{pkg.version}</span>
                    {#if pkg.url}
                      <a
                        href={pkg.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        class="text-sm text-green-600 dark:text-green-400 hover:underline ml-auto"
                      >
                        {$_(`pages.licenses.packages.link`)}
                      </a>
                    {/if}
                  </div>
                {/each}
              </div>
            </div>
          {/each}
        </section>
      </div>
    {/if}
  </div>
{/if}
